import './UserMenu.scss';

import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import {
  NULL_VALUE,
  ROUTE_ACCOUNT_DOCUMENTATION,
  ROUTE_ACCOUNT_INFORMATIONS,
  ROUTE_ACCOUNT_TOP_PRODUCTS,
  ROUTE_ACCOUNT_WORKSITES,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import useTheme from '@/common/hooks/useTheme';
import { ReactComponent as EuroSvg } from '@/ebat/assets/icons/euro.svg';
import BackMenuButton from '@/ebat/components/blocks/Header/BackMenuButton/BackMenuButton';
import NotificationsButton from '@/ebat/components/blocks/Header/Notifications/NotificationsButton/NotificationsButton';
import UserMenuNavLink from '@/ebat/components/blocks/Header/UserMenu/UserMenuNavLink/UserMenuNavLink';
import PricesRequest from '@/ebat/components/blocks/Modal/Common/PricesRequest/PricesRequest';
import { useHeaderAccessesContext } from '@/ebat/contexts/Header/HeaderAccessesContext/HeaderAccessesContext';
import { useHeaderMenuContext } from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';

import { UserMenuDefaultProps, UserMenuPropTypes } from './UserMenu.proptypes';

const getUserName = (user) => {
  let firstName = user?.firstname ?? null;
  if (firstName === NULL_VALUE) {
    firstName = null;
  }
  let lastName = user?.lastname ?? null;
  if (lastName === NULL_VALUE) {
    lastName = null;
  }
  if (!firstName && !lastName) {
    return null;
  }
  if (firstName && !lastName) {
    return firstName;
  }
  if (!firstName && lastName) {
    return lastName;
  }

  return `${firstName} ${lastName}`;
};

const getUserCompany = (user) => user?.customer_name ?? null;

export const getUserStore = (user) => {
  const label = [];
  if (user?.company) {
    label.push(user?.company);
  }
  if (user?.warehouse && user?.warehouse?.name) {
    label.push(user?.warehouse?.name);
  }

  return label.join(' - ');
};

const UserMenu = ({ className }) => {
  const { t } = useTranslation();
  const { user, showDoc, logout } = useAuth();
  const { onOpenModal, setModalClassName, setModalContent } = useModalContext();
  const { handleClose } = useHeaderMenuContext();
  const {
    partner: hasPartnerAccesses,
    infos: hasInfosAccesses,
    topProducts: hasTopProductsAccesses,
    worksites: hasWorksitesAccesses,
    priceFile: hasPriceFileAccesses,
  } = useHeaderAccessesContext();
  const { PartnerLogoWhite, partnerHost } = useTheme();
  const userName = getUserName(user);
  const userCompany = getUserCompany(user);
  const userStore = getUserStore(user);

  const handleOpenPrice = () => {
    handleClose();
    setModalContent(<PricesRequest user={user} />);
    setModalClassName('modal-prices-request');
    onOpenModal();
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <div
      className={cn(className, 'user-menu', {
        'user-menu--with-partner': hasPartnerAccesses,
      })}
      data-qa-id="submenu-user"
    >
      <BackMenuButton
        className="user-menu__back-button"
        label={t('common:entities:layout.header.my_account.label')}
        onClick={handleClose}
      />
      {(userName || userCompany || userStore) && (
        <p className="user-menu__info">
          {userName && <span className="user-menu__info-name">{userName}</span>}
          {userCompany && (
            <span className="user-menu__info-company">{userCompany}</span>
          )}
          {userStore && (
            <span className="user-menu__info-store">{userStore}</span>
          )}
        </p>
      )}
      <UserMenuNavLink
        className="user-menu__link-account"
        context={cn({
          'user-menu-link-information-disabled': !hasInfosAccesses,
          'user-menu-link-information-active': hasInfosAccesses,
        })}
        disabled={!hasInfosAccesses}
        label={t('common:entities:layout.header.my_account.information')}
        to={ROUTE_ACCOUNT_INFORMATIONS}
      />
      <div className="user-menu__top-divider" />
      <UserMenuNavLink
        context={cn({
          'user-menu-link-worksite-disabled': !hasWorksitesAccesses,
          'user-menu-link-worksite-active': hasWorksitesAccesses,
        })}
        disabled={!hasWorksitesAccesses}
        label={t('common:entities:layout.header.my_account.construction')}
        to={ROUTE_ACCOUNT_WORKSITES}
      />
      <UserMenuNavLink
        context={cn({
          'user-menu-link-top-product-disabled': !hasTopProductsAccesses,
          'user-menu-link-top-product-active': hasTopProductsAccesses,
        })}
        disabled={!hasTopProductsAccesses}
        label={t('ebat:entities:layout.header.my_account.awards')}
        to={ROUTE_ACCOUNT_TOP_PRODUCTS}
      />
      {showDoc && (
        <UserMenuNavLink
          context="user-menu-link-documentation"
          label={t('ebat:entities:layout.header.my_account.documents')}
          to={ROUTE_ACCOUNT_DOCUMENTATION}
        />
      )}
      <div className="user-menu__bottom-divider" />
      <NotificationsButton className="user-menu__action user-menu__action--notification" />
      <Button
        className={cn('user-menu__action', {
          'user-menu__action--disabled': !hasPriceFileAccesses,
        })}
        onClick={handleOpenPrice}
        disabled={!hasPriceFileAccesses}
        context={cn({
          'user-menu-price-file-accesses-disabled': !hasPriceFileAccesses,
          'user-menu-price-file-accesses-active': hasPriceFileAccesses,
        })}
      >
        <EuroSvg className="user-menu__action-icon" />
        <span className="user-menu__action-label">
          {t('ebat:entities:layout.header.my_account.price_information')}
        </span>
      </Button>
      <Button
        className="user-menu__action user-menu__action--logout"
        context="user-menu-logout"
        onClick={handleLogout}
      >
        <span className="user-menu__action-label">
          {t('common:messages:actions.logout')}
        </span>
      </Button>
      {hasPartnerAccesses && (
        <a
          className="user-menu__partner-link"
          href={partnerHost}
          onClick={(e) => {
            e.stopPropagation();
          }}
          rel="noreferrer"
          data-qa-id="user-menu-link-partner-link"
        >
          <span className="user-menu__partner-label">
            {t('ebat:entities:layout.header.partner_access')}
          </span>
          <PartnerLogoWhite className="user-menu__partner-logo" />
        </a>
      )}
    </div>
  );
};

UserMenu.defaultProps = {
  ...UserMenuDefaultProps,
};

UserMenu.propTypes = {
  ...UserMenuPropTypes,
};

export default UserMenu;
